/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  FileText,
  RotateCcw,
  SkipForward,
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import type { SelectedCitation } from '@/components/agent-chat/context/AgentChatContext';
import { useOptionalAgentChat } from '@/components/agent-chat/context/AgentChatContext';
import { DeliverableMatchCard } from '@/components/citations';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { cn } from '@/lib/utils';
import { Z_INDEX_MODAL_HIGH } from '@/components/ui/zIndex';
import {
  useBypassDeliverableMutation,
  usePartialUpdateTaskDeliverableMutation,
} from '@/services/api/deliverablesService';
import {
  Deliverable,
  DeliverableStatus,
} from '@/services/types/deliverables-types';

interface DeliverableItemProps {
  deliverable: Deliverable;
  matterId?: string;
  taskId?: string;
  onUpdate?: () => void;
  setSelectedCitation?: (citation: SelectedCitation | null) => void;
}

const DeliverableItem: React.FC<DeliverableItemProps> = ({
  deliverable,
  matterId,
  taskId,
  onUpdate,
  setSelectedCitation,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [updateDeliverable] = usePartialUpdateTaskDeliverableMutation();
  const [bypassDeliverable] = useBypassDeliverableMutation();

  const isSatisfied = deliverable.status === DeliverableStatus.SATISFIED;
  const isBypassed = deliverable.status === DeliverableStatus.BYPASSED;
  const isPending =
    deliverable.status === DeliverableStatus.PENDING || !deliverable.status;
  const hasActions = matterId && taskId;
  const hasMatches = deliverable.matches && deliverable.matches.length > 0;

  const getStatusInfo = () => {
    switch (deliverable.status) {
      case DeliverableStatus.SATISFIED:
        return {
          icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
          label: 'Found',
          color: 'text-green-700 bg-green-50 border-green-200',
        };
      case DeliverableStatus.BYPASSED:
        return {
          icon: <SkipForward className="h-4 w-4 text-amber-600" />,
          label: 'Skipped',
          color: 'text-amber-700 bg-amber-50 border-amber-200',
        };
      default:
        return {
          icon: <Circle className="h-4 w-4 text-gray-400" />,
          label: 'Looking...',
          color: 'text-gray-700 bg-gray-50 border-gray-200',
        };
    }
  };

  const handleMarkDone = async () => {
    if (!hasActions) return;
    try {
      await updateDeliverable({
        matterId: matterId!,
        taskId: taskId!,
        deliverableId: deliverable.id,
        deliverable: { status: DeliverableStatus.SATISFIED },
      });
      toast.success(`"${deliverable.name}" marked as found`);
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update deliverable');
    }
  };

  const handleSkip = async () => {
    if (!hasActions) return;
    try {
      await bypassDeliverable({
        matterId: matterId!,
        taskId: taskId!,
        deliverableId: deliverable.id,
      });
      toast.success(`"${deliverable.name}" skipped`);
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to skip deliverable');
    }
  };

  const handleUndo = async () => {
    if (!hasActions) return;
    try {
      await updateDeliverable({
        matterId: matterId!,
        taskId: taskId!,
        deliverableId: deliverable.id,
        deliverable: { status: DeliverableStatus.PENDING },
      });
      toast.success(`"${deliverable.name}" reset to searching`);
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to reset deliverable');
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Main Row */}
      <button
        type="button"
        className="w-full p-4 cursor-pointer hover:bg-gray-50 transition-colors text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Status */}
            <div
              className={cn(
                'flex items-center gap-1.5 text-xs px-2 py-1 rounded border font-medium',
                statusInfo.color,
              )}
            >
              {statusInfo.icon}
              {statusInfo.label}
            </div>

            {/* File Name */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">
                {deliverable.name}
              </h3>
              {hasMatches && (
                <div className="text-xs text-blue-600 mt-0.5">
                  {deliverable.matches!.length} potential match
                  {deliverable.matches!.length !== 1 ? 'es' : ''} found
                </div>
              )}
            </div>

            {/* Expand Toggle */}
            <div className="text-gray-400">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </div>
          </div>

          {/* Quick Actions */}
          {hasActions && (
            <div
              className="flex gap-1 ml-3"
              onClick={(e) => e.stopPropagation()}
            >
              {isPending ? (
                <>
                  <Button
                    onClick={handleMarkDone}
                    size="sm"
                    className="text-xs h-7"
                  >
                    Mark Found
                  </Button>
                  <Button
                    onClick={handleSkip}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                  >
                    Skip
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleUndo}
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t bg-gray-50/50">
          <div className="p-4 space-y-4">
            {/* What We're Looking For */}
            {deliverable.prompt && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  What we&apos;re looking for:
                </h4>
                <div className="text-sm text-gray-700 leading-relaxed bg-white p-3 rounded border">
                  {deliverable.prompt}
                </div>
              </div>
            )}

            {/* What We Found */}
            {hasMatches ? (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  What we found ({deliverable.matches!.length}):
                </h4>
                <div className="space-y-3">
                  {deliverable.matches!.map((match) => (
                    <DeliverableMatchCard
                      key={match.id}
                      match={match}
                      matterId={matterId || ''}
                      setSelectedCitation={setSelectedCitation}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <AlertCircle className="h-6 w-6 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No matches found yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  Finch is still looking for this file
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface DeliverablesModalProps {
  /** List of deliverables to display */
  deliverables: Deliverable[];
  /** Whether the modal is open */
  open: boolean;
  /** Function to call when modal should close */
  onOpenChange: (open: boolean) => void;
  /** Matter ID for API calls */
  matterId?: string;
  /** Task ID for API calls */
  taskId?: string;
  /** Function to call when deliverables are updated */
  onUpdate?: () => void;
  /** Optional title override */
  title?: string;
  /** Optional description override */
  description?: string;
}

export const DeliverablesModal: React.FC<DeliverablesModalProps> = ({
  deliverables,
  open,
  onOpenChange,
  matterId,
  taskId,
  onUpdate,
  title = 'Task Requirements',
  description = 'Finch expects to find these files to close this task',
}) => {
  const optionalAgentChat = useOptionalAgentChat();
  const setSelectedCitation = optionalAgentChat?.setSelectedCitation;

  // Sort deliverables: pending first, then by name
  const sortedDeliverables = [...deliverables].sort((a, b) => {
    if (
      a.status === DeliverableStatus.PENDING &&
      b.status !== DeliverableStatus.PENDING
    )
      return -1;
    if (
      a.status !== DeliverableStatus.PENDING &&
      b.status === DeliverableStatus.PENDING
    )
      return 1;
    return a.name.localeCompare(b.name);
  });

  const stats = {
    total: deliverables.length,
    found: deliverables.filter((d) => d.status === DeliverableStatus.SATISFIED)
      .length,
    skipped: deliverables.filter((d) => d.status === DeliverableStatus.BYPASSED)
      .length,
    pending: deliverables.filter(
      (d) => d.status === DeliverableStatus.PENDING || !d.status,
    ).length,
    withMatches: deliverables.filter((d) => d.matches && d.matches.length > 0)
      .length,
  };

  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} size="4xl" zIndex={Z_INDEX_MODAL_HIGH}>
      <DialogTitle className="flex items-start gap-3">
        <FileText className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </DialogTitle>

      <DialogBody className="max-h-[70vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Progress Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-sm">
                  <span className="font-medium text-blue-900">
                    {stats.found + stats.skipped}/{stats.total} Complete
                  </span>
                  <span className="text-blue-700 ml-2">
                    ({stats.found} found, {stats.skipped} skipped)
                  </span>
                </div>
              </div>
              {stats.withMatches > 0 && (
                <div className="text-xs text-blue-700">
                  {stats.withMatches} file{stats.withMatches !== 1 ? 's' : ''}{' '}
                  have potential matches
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="bg-green-500 h-full transition-all duration-300"
                  style={{ width: `${(stats.found / stats.total) * 100}%` }}
                />
                <div
                  className="bg-amber-400 h-full transition-all duration-300"
                  style={{ width: `${(stats.skipped / stats.total) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Deliverables List */}
          {sortedDeliverables.length > 0 ? (
            <div className="space-y-3">
              {sortedDeliverables.map((deliverable) => (
                <DeliverableItem
                  key={deliverable.id}
                  deliverable={deliverable}
                  matterId={matterId}
                  taskId={taskId}
                  onUpdate={onUpdate}
                  setSelectedCitation={setSelectedCitation}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="font-medium text-gray-900 mb-2">
                No files required
              </h3>
              <p className="text-sm">
                This task has no specific file requirements.
              </p>
            </div>
          )}
        </div>
      </DialogBody>

      <DialogActions>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
